import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { gql } from "@apollo/client"
import type { CountryIntelligenceRulesTimeOff } from "@oysterhr/schema"
import { concatPhrase } from "../../hooks/useKBAdvisor/helpers"
import { TimeOffRulesFragment } from "../../hooks/useKBIntegration/fragments"
import type { AdvisorKnowledgeBase } from "../../types"
import useSubdivisionName from "~/app/queries/useSubdivisionName"

export type Module = AdvisorKnowledgeBase<{
  name: string
  isoAlpha2Code: string
  nationalHolidaysPerYear: number
  statutoryTimeOff?: CountryIntelligenceRulesTimeOff[]
  customaryTimeOff?: CountryIntelligenceRulesTimeOff[]
  recommendedTimeOff?: CountryIntelligenceRulesTimeOff[]
  topOfMarketTimeOff?: CountryIntelligenceRulesTimeOff[]
}>

export const queryFragment: Module["queryFragment"] = gql`
  fragment kbHolidays on CountryIntelligence {
    name
    isoAlpha2Code
    nationalHolidaysPerYear

    statutoryTimeOff: timeOffRules(oysterScore: STATUTORY, type: VACATIONS) {
      ...TimeOffRulesFragment
    }
    customaryTimeOff: timeOffRules(oysterScore: CUSTOMARY, type: VACATIONS) {
      ...TimeOffRulesFragment
    }
    recommendedTimeOff: timeOffRules(oysterScore: RECOMMENDED, type: VACATIONS) {
      ...TimeOffRulesFragment
    }
    topOfMarketTimeOff: timeOffRules(oysterScore: TOP_OF_MARKET, type: VACATIONS) {
      ...TimeOffRulesFragment
    }
  }

  ${TimeOffRulesFragment}
`

export const TranslationComponent: Module["TranslationComponent"] = ({ data, input }) => {
  const { t } = useTranslation("knowledge_base/HiringAdvisor")
  const subdivisionCode = input?.subdivisionCode
  const subdivisionName = useSubdivisionName(data.isoAlpha2Code || "", subdivisionCode || "")

  const phrases = useMemo(() => {
    // @check-later: this is assuming it's always days
    const statutoryDays = data?.statutoryTimeOff?.[0]?.amountPerYear?.value
    const topOfMarketDays = data?.topOfMarketTimeOff?.[0]?.amountPerYear?.value
    const customaryDays = data?.customaryTimeOff?.[0]?.amountPerYear?.value
    const recommendedDays = data?.recommendedTimeOff?.[0]?.amountPerYear?.value
    const nationalHolidaysPerYear = data?.nationalHolidaysPerYear

    let content: string[] = []
    content = concatPhrase(t, content, "timeOff.base", {
      nationalHolidaysPerYear,
      countryName: data?.name,
    })
    content = concatPhrase(t, content, "timeOff.statutory", { statutoryDays })
    content = concatPhrase(t, content, "timeOff.topOfMarket", { topOfMarketDays })
    content = concatPhrase(t, content, "timeOff.customary", { customaryDays })
    content = concatPhrase(t, content, "timeOff.recommended", { recommendedDays })

    return content || []
  }, [data, t])

  if (!phrases.length) {
    throw new Error("No time off rules")
  }

  return (
    <div>
      {t("timeOff.summary", {
        countryName: [data?.name, subdivisionName].filter(Boolean).join(", "),
      })}
      <ul className="list-inside list-disc">
        {phrases.map((phrase, index) => (
          <li key={index}>{phrase}</li>
        ))}
      </ul>
    </div>
  )
}

export const checkDataPresence: Module["checkDataPresence"] = data => {
  return !!(
    data.statutoryTimeOff?.length ||
    data.topOfMarketTimeOff?.length ||
    data.customaryTimeOff?.length ||
    data.recommendedTimeOff?.length
  )
}
